import React from "react";
import { useLocation } from "react-router-dom";
import { DragDropContext, Droppable, Draggable, type DropResult } from "@hello-pangea/dnd";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";

import { Task, TaskPriority } from "./types";
import { TaskCard } from "./TaskCard";
import { NewTaskDialog } from "./NewTaskDialog";
import { AddColumnInline } from "./AddColumnInline";
import { ColumnMenu } from "./ColumnMenu";
import { TaskDetailDialog } from "./TaskDetailDialog";
import { BoardCalendarView } from "./BoardCalendarView";
import { BoardListView } from "./BoardListView";
import { HeaderSettingsDialog } from "./HeaderSettingsDialog";
import { Plus, Trash2, Archive, Search, X, Calendar as CalendarIcon, Undo2, LayoutDashboard, Table2, Minimize2, Maximize2, ArrowLeft } from "lucide-react";
import { format, isAfter, isBefore, subDays, subMonths, subYears } from "date-fns";
import { cn } from "@/lib/utils";
import type { DateRange } from "react-day-picker";
import { type TaskBoard } from "@/lib/boardService";
import { useBoardState } from "@/hooks/useBoardState";
import { Skeleton } from "@/components/ui/skeleton";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAuth } from "@/context/AuthContext";

// Dynamic columns are defined in state; archived tasks are hidden from the board

const getPriorityColor = (priority: string) => {
  switch (priority) {
    case "critical": return "destructive";
    case "high": return "secondary";
    case "medium": return "default";
    case "low": return "outline";
    default: return "outline";
  }
};

const getPriorityText = (priority: string) => {
  return priority.charAt(0).toUpperCase() + priority.slice(1);
};

const SkeletonTaskCard = ({ compact = false }: { compact?: boolean }) => (
  <div className="rounded-lg bg-card border border-border p-3 flex flex-col gap-2">
    <div className="flex items-start justify-between gap-2">
      <Skeleton className="h-4 w-3/4" />
      <Skeleton className="h-5 w-14 rounded-full" />
    </div>
    {!compact && <Skeleton className="h-3 w-1/2" />}
    <div className="flex items-center justify-between mt-1">
      <Skeleton className="h-3 w-20" />
      <Skeleton className="h-6 w-6 rounded-full" />
    </div>
  </div>
);

type KanbanBoardProps = {
  board: TaskBoard;

  newTaskDialogOpen?: boolean;
  onNewTaskDialogOpenChange?: (open: boolean) => void;
  onBack?: () => void;
  headerConfig?: { title: string; description: string; background: any; hideBanner?: boolean; enableUpvotes?: boolean };
  onHeaderUpdate?: (updates: { title?: string; description?: string; background?: any; slug?: string; hideBanner?: boolean; enableUpvotes?: boolean }) => Promise<void>;
  currentSlug?: string;
};

export const KanbanBoard: React.FC<KanbanBoardProps> = ({ board, newTaskDialogOpen, onNewTaskDialogOpenChange, onBack, headerConfig, onHeaderUpdate, currentSlug }) => {
  const isMobile = useIsMobile();
  const { user } = useAuth();

  // Use the new database-backed state hook
  const {
    state,
    loading,
    error,
    addTask: addTaskDB,
    updateTask: updateTaskDB,
    deleteTask: deleteTaskDB,
    archiveTask: archiveTaskDB,
    unarchiveTask: unarchiveTaskDB,
    moveTask: moveTaskDB,
    addColumn: addColumnDB,
    updateColumnTitle: updateColumnTitleDB,
    updateColumnBgColor: updateColumnBgColorDB,
    deleteColumn: deleteColumnDB,
    reorderColumns: reorderColumnsDB,
    toggleUpvote,
  } = useBoardState(board.id);

  const enableUpvotes = Boolean(
    headerConfig?.enableUpvotes ??
    (board as any)?.enable_upvotes ??
    (board as any)?.enableUpvotes ??
    (board?.background as any)?.enableUpvotes
  );

  const effectiveHeaderConfig = React.useMemo(() => {
    if (!headerConfig) return undefined;
    return {
      ...headerConfig,
      enableUpvotes,
    };
  }, [headerConfig, enableUpvotes]);

  const [showAddColumn, setShowAddColumn] = React.useState(false);
  const [isArchiveView, setIsArchiveView] = React.useState(false);
  const [columnTaskDialogOpen, setColumnTaskDialogOpen] = React.useState<string | null>(null);
  const [viewMode, setViewMode] = React.useState<"kanban" | "calendar" | "list">("kanban");
  const [searchTerm, setSearchTerm] = React.useState("");
  const [dateFilter, setDateFilter] = React.useState<"7days" | "1month" | "6months" | "1year" | "custom">("1month");
  const [customDateRange, setCustomDateRange] = React.useState<DateRange | undefined>();
  const [showCustomCalendar, setShowCustomCalendar] = React.useState(false);
  const [archiveSearchOpen, setArchiveSearchOpen] = React.useState(false);
  const archiveSearchInputRef = React.useRef<HTMLInputElement>(null);
  const [boardSearchOpen, setBoardSearchOpen] = React.useState(false);
  const [boardSearchTerm, setBoardSearchTerm] = React.useState("");
  const boardSearchInputRef = React.useRef<HTMLInputElement>(null);
  const [selectedTasks, setSelectedTasks] = React.useState<Set<string>>(new Set());
  const [selectedTask, setSelectedTask] = React.useState<Task | null>(null);
  const [taskDetailOpen, setTaskDetailOpen] = React.useState(false);
  const [dialogKey, setDialogKey] = React.useState(0);
  const [calendarTaskDialogOpen, setCalendarTaskDialogOpen] = React.useState(false);
  const [calendarTaskDate, setCalendarTaskDate] = React.useState<Date | null>(null);
  const [collapsedColumns, setCollapsedColumns] = React.useState<Set<string>>(new Set());

  const location = useLocation();

  // Handle deep linking to task details
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const taskId = params.get('task');
    
    if (taskId && state.tasksById[taskId]) {
      setSelectedTask(state.tasksById[taskId]);
      setTaskDetailOpen(true);
    }
  }, [location.search, state.tasksById]);

  const addTask = async (data: { title: string; description?: string; priority: TaskPriority; startDate?: string; dueDate?: string; assignees?: { id: string; name: string; email: string; avatar?: string }[]; targetColumnId?: string }) => {
    try {
      await addTaskDB(data);
    } catch (err) {
      console.error('Error adding task:', err);
    }
  };

  const deleteTask = async (id: string) => {
    try {
      await deleteTaskDB(id);
      // Clear selected task if it's the one being deleted
      if (selectedTask?.id === id) {
        setSelectedTask(null);
        setTaskDetailOpen(false);
      }
    } catch (err) {
      console.error('Error deleting task:', err);
    }
  };

  const archiveTask = async (id: string) => {
    try {
      await archiveTaskDB(id);
      // Clear selected task if it's the one being archived
      if (selectedTask?.id === id) {
        setSelectedTask(null);
        setTaskDetailOpen(false);
      }
    } catch (err) {
      console.error('Error archiving task:', err);
    }
  };

  const archiveAllFromLastColumn = async () => {
    try {
      // Get the last column
      const lastColumnId = state.columnOrder[state.columnOrder.length - 1];
      if (!lastColumnId) return;

      const lastColumnTaskIds = state.columns[lastColumnId] || [];
      if (lastColumnTaskIds.length === 0) return;

      // Archive all tasks from the last column
      await Promise.all(lastColumnTaskIds.map(id => archiveTaskDB(id)));
    } catch (err) {
      console.error('Error archiving all tasks from last column:', err);
    }
  };

  const moveColumnToLast = async (columnId: string) => {
    try {
      // Get the last column
      const lastColumnId = state.columnOrder[state.columnOrder.length - 1];
      if (!lastColumnId || lastColumnId === columnId) return;

      const taskIds = state.columns[columnId] || [];
      if (taskIds.length === 0) return;

      // Move all tasks to the last column
      await Promise.all(
        taskIds.map((id, index) => moveTaskDB(id, lastColumnId, index))
      );
    } catch (err) {
      console.error('Error moving column to last:', err);
    }
  };

  const unarchiveTask = async (id: string) => {
    try {
      await unarchiveTaskDB(id);
    } catch (err) {
      console.error('Error unarchiving task:', err);
    }
  };

  const permanentlyDeleteTask = async (id: string) => {
    try {
      await deleteTaskDB(id);
    } catch (err) {
      console.error('Error permanently deleting task:', err);
    }
  };

  const addColumn = async (title: string, bgColor?: string) => {
    try {
      await addColumnDB(title, bgColor);
    } catch (err) {
      console.error('Error adding column:', err);
      throw err;
    }
  };

  const deleteColumn = async (id: string) => {
    try {
      // Prevent deleting protected default columns
      const protectedTitles = new Set(["Open", "To Do", "In Progress", "Done"]);
      const columnTitle = state.columnMeta[id]?.title;
      if (protectedTitles.has(columnTitle)) {
        return;
      }
      await deleteColumnDB(id);
    } catch (err) {
      console.error('Error deleting column:', err);
      throw err;
    }
  };
  const onDragEnd = async (result: DropResult) => {
    const { destination, source, draggableId, type } = result;
    if (!destination) return;

    // Reorder columns
    if (type === "COLUMN") {
      if (source.index === destination.index) return;

      try {
        const newColumnOrder = Array.from(state.columnOrder);
        const [removed] = newColumnOrder.splice(source.index, 1);
        newColumnOrder.splice(destination.index, 0, removed);

        await reorderColumnsDB(newColumnOrder);
      } catch (err) {
        console.error('Error reordering columns:', err);
      }
      return;
    }

    // Move tasks between columns
    const from = source.droppableId;
    const to = destination.droppableId;

    if (from === to && source.index === destination.index) return;

    try {
      await moveTaskDB(draggableId, to, destination.index);
    } catch (err) {
      console.error('Error moving task:', err);
    }
  };

  const updateColumnTitle = async (columnId: string, title: string) => {
    try {
      await updateColumnTitleDB(columnId, title);
    } catch (err) {
      console.error('Error updating column title:', err);
    }
  };

  const updateColumnBgColor = async (columnId: string, bgColor: string) => {
    try {
      await updateColumnBgColorDB(columnId, bgColor);
    } catch (err) {
      console.error('Error updating column color:', err);
    }
  };

  const Column: React.FC<{ colId: string }> = ({ colId }) => {
    const ids = state.columns[colId] || [];
    const filteredTaskIds = React.useMemo(() => {
      if (!boardSearchTerm.trim()) return ids;
      const lower = boardSearchTerm.toLowerCase();
      return ids.filter((id) => {
        const task = state.tasksById[id];
        if (!task) return false;
        return (
          task.title?.toLowerCase().includes(lower) ||
          task.description?.toLowerCase().includes(lower) ||
          task.assignees?.some(a => a.name?.toLowerCase().includes(lower)) ||
          task.tags?.some((t: string) => t.toLowerCase().includes(lower))
        );
      });
    }, [ids, boardSearchTerm, state.tasksById]);
    const columnMeta = state.columnMeta[colId];
    const bgColor = columnMeta?.bgColor || "";
    const isCustomColor = Boolean(bgColor && (bgColor.startsWith("#") || bgColor.startsWith("rgb") || bgColor.startsWith("hsl")));
    const isProtectedColumn = ["open", "todo", "inprogress", "done"].includes(colId);
    const [isEditing, setIsEditing] = React.useState(false);
    const [editTitle, setEditTitle] = React.useState(columnMeta?.title ?? colId);
    const isCollapsed = collapsedColumns.has(colId);

    const toggleCollapse = () => {
      setCollapsedColumns(prev => {
        const next = new Set(prev);
        if (next.has(colId)) {
          next.delete(colId);
        } else {
          next.add(colId);
        }
        return next;
      });
    };

    const handleDoubleClick = () => {
      if (!isCollapsed) {
        setIsEditing(true);
        setEditTitle(columnMeta?.title ?? colId);
      }
    };

    const handleSaveTitle = () => {
      if (editTitle.trim() && editTitle !== columnMeta?.title) {
        updateColumnTitle(colId, editTitle.trim());
      }
      setIsEditing(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        handleSaveTitle();
      } else if (e.key === "Escape") {
        setEditTitle(columnMeta?.title ?? colId);
        setIsEditing(false);
      }
    };

    if (isCollapsed) {
      return (
        <Card
          style={isCustomColor ? { backgroundColor: bgColor } : undefined}
          className={`border border-border shadow-sm h-full transition-all group ${!isCustomColor ? (bgColor || "bg-board-column") : ""} w-14 cursor-pointer hover:bg-accent/30`}
          onClick={toggleCollapse}
        >
          <CardContent className="p-2 h-full flex flex-col items-center">
            <div className="flex flex-col items-center gap-2 h-full">
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 flex-shrink-0"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleCollapse();
                }}
                title="Expand column"
              >
                <Maximize2 className="h-3.5 w-3.5" />
              </Button>
              <div className="flex-1" />
              <div className="writing-mode-vertical text-center flex flex-col-reverse items-center gap-2 mb-2">
                <span className="text-sm font-semibold whitespace-nowrap" style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', transform: 'rotate(180deg)' }}>
                  {columnMeta?.title ?? colId}
                </span>
                <span className="text-xs text-muted-foreground" style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', transform: 'rotate(180deg)' }}>
                  {filteredTaskIds.length}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card
        style={isCustomColor ? { backgroundColor: bgColor } : undefined}
        className={`border border-border shadow-sm w-full transition-all group ${!isCustomColor ? (bgColor || "bg-board-column") : ""}`}
      >
        <CardHeader className="pb-2 px-3 pt-3">
          <CardTitle className="flex items-center justify-between text-base">
            <span className="flex items-center gap-2">
              {isEditing ? (
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  onBlur={handleSaveTitle}
                  onKeyDown={handleKeyDown}
                  className="bg-background/80 border border-workspace-accent rounded px-2 py-1 font-semibold text-base w-full focus:outline-none focus:ring-2 focus:ring-workspace-accent/50"
                  autoFocus
                  onFocus={(e) => e.target.select()}
                  placeholder="Enter column name..."
                />
              ) : (
                <span
                  onDoubleClick={handleDoubleClick}
                  className="cursor-pointer hover:bg-accent/50 rounded px-1 -mx-1 transition-colors duration-200 hover:text-workspace-accent dark:hover:text-workspace-accent"
                  title="Double-click to rename"
                >
                  {columnMeta?.title ?? colId}
                </span>
              )}
              <span className="text-xs text-muted-foreground">{filteredTaskIds.length}</span>
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 opacity-60 hover:opacity-100"
                onClick={toggleCollapse}
                title="Collapse column"
              >
                <Minimize2 className="h-3.5 w-3.5" />
              </Button>
              <ColumnMenu
                columnId={colId}
                currentTitle={columnMeta?.title ?? colId}
                currentBgColor={bgColor}
                isLastColumn={colId === state.columnOrder[state.columnOrder.length - 1]}
                lastColumnTitle={state.columnMeta[state.columnOrder[state.columnOrder.length - 1]]?.title || 'Last Column'}
                isProtectedColumn={isProtectedColumn}
                onUpdateTitle={updateColumnTitle}
                onUpdateBgColor={updateColumnBgColor}
                onArchiveAll={colId === state.columnOrder[state.columnOrder.length - 1] ? archiveAllFromLastColumn : undefined}
                onMoveToLast={colId !== state.columnOrder[state.columnOrder.length - 1] ? () => moveColumnToLast(colId) : undefined}
                hasTasksToArchive={ids.length > 0}
                hasTasksToMove={ids.length > 0}
                onRename={handleDoubleClick}
              />
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="px-3 pb-3">
          <Droppable droppableId={colId}>
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className="min-h-[120px] space-y-1.5 rounded-md p-1 transition-colors bg-transparent"
              >
                {filteredTaskIds.map((id, index) => {
                  const task = state.tasksById[id];
                  if (!task) return null;
                  return (
                    <Draggable key={id} draggableId={id} index={index}>
                      {(dragProvided) => (
                        <div ref={dragProvided.innerRef} {...dragProvided.draggableProps} {...dragProvided.dragHandleProps}>
                          <TaskCard
                            task={task}
                            onDelete={deleteTask}
                            onArchive={archiveTask}
                            showArchive={colId === state.columnOrder[state.columnOrder.length - 1]}
                            onTaskClick={handleTaskClick}
                            onToggleComplete={handleToggleComplete}
                            enableUpvotes={enableUpvotes}
                            onToggleUpvote={toggleUpvote}
                            currentUserId={user?.id}
                          />
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
          <button
            onClick={() => setColumnTaskDialogOpen(colId)}
            className="mt-2 w-full flex items-center gap-2 px-2 py-2 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-accent/50 transition-colors group"
          >
            <Plus className="h-4 w-4 opacity-60 group-hover:opacity-100 transition-opacity" />
            <span>Add task</span>
          </button>
          <NewTaskDialog
            onAdd={addTask}

            open={columnTaskDialogOpen === colId}
            onOpenChange={(open) => setColumnTaskDialogOpen(open ? colId : null)}
            hideTrigger
            targetColumnId={colId}
          />
        </CardContent>
      </Card>
    );
  };

  // Get archived tasks for archive view
  const archivedTasks = React.useMemo(() => {
    return state.archived
      .map((id) => state.tasksById[id])
      .filter((task): task is Task => task !== undefined && task.status === "archived");
  }, [state]);

  // Filter archived tasks based on search and date
  const filteredArchivedTasks = React.useMemo(() => {
    let filtered = archivedTasks;

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (task) =>
          task.title.toLowerCase().includes(term) ||
          (task.description && task.description.toLowerCase().includes(term))
      );
    }

    // Date filter
    if (dateFilter !== "custom") {
      const now = new Date();
      let cutoffDate: Date;

      switch (dateFilter) {
        case "7days":
          cutoffDate = subDays(now, 7);
          break;
        case "1month":
          cutoffDate = subMonths(now, 1);
          break;
        case "6months":
          cutoffDate = subMonths(now, 6);
          break;
        case "1year":
          cutoffDate = subYears(now, 1);
          break;
        default:
          cutoffDate = subMonths(now, 1);
      }

      filtered = filtered.filter((task) => {
        if (!task.archivedAt) return true;
        const archivedDate = new Date(task.archivedAt);
        return isAfter(archivedDate, cutoffDate);
      });
    } else if (customDateRange?.from || customDateRange?.to) {
      filtered = filtered.filter((task) => {
        if (!task.archivedAt) return true;
        const archivedDate = new Date(task.archivedAt);
        
        if (customDateRange?.from && isBefore(archivedDate, customDateRange.from)) {
          return false;
        }
        if (customDateRange?.to && isAfter(archivedDate, customDateRange.to)) {
          return false;
        }
        return true;
      });
    }

    // Sort by archived date (newest first)
    return filtered.sort((a, b) => {
      if (!a.archivedAt && !b.archivedAt) return 0;
      if (!a.archivedAt) return 1;
      if (!b.archivedAt) return -1;
      return new Date(b.archivedAt).getTime() - new Date(a.archivedAt).getTime();
    });
  }, [archivedTasks, searchTerm, dateFilter, customDateRange]);

  const toggleArchiveView = () => {
    setIsArchiveView(!isArchiveView);
    // Reset filters and selections when switching views
    if (!isArchiveView) {
      setSearchTerm("");
      setDateFilter("1month");
      setCustomDateRange(undefined);
      setSelectedTasks(new Set());
    }
  };

  const handleSelectTask = (taskId: string, checked: boolean) => {
    setSelectedTasks(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(taskId);
      } else {
        newSet.delete(taskId);
      }
      return newSet;
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedTasks(new Set(filteredArchivedTasks.map(task => task.id)));
    } else {
      setSelectedTasks(new Set());
    }
  };

  const handleBulkUnarchive = () => {
    if (selectedTasks.size === 0) return;
    
    selectedTasks.forEach(taskId => {
      unarchiveTask(taskId);
    });
    setSelectedTasks(new Set());
  };

  const handleBulkDelete = () => {
    if (selectedTasks.size === 0) return;
    
    selectedTasks.forEach(taskId => {
      permanentlyDeleteTask(taskId);
    });
    setSelectedTasks(new Set());
  };

  const handleTaskClick = (task: Task) => {
    setSelectedTask(task);
    setTaskDetailOpen(true);
  };

  const handleToggleComplete = async (taskId: string, completed: boolean) => {
    try {
      await updateTaskDB(taskId, {
        completed,
        completedAt: completed ? new Date().toISOString() : null,
      });
    } catch (err) {
      console.error('Error toggling task completion:', err);
    }
  };

  const updateTask = async (taskId: string, updates: Partial<Task>) => {
    try {
      // Use the hook's updateTaskDB which handles both DB update and optimistic state update
      await updateTaskDB(taskId, updates);

      // Update selectedTask if it's the same task being updated
      // Use functional setState to avoid closure stale data issue
      setSelectedTask(prev => {
        // Check against current state, not closure value
        if (prev?.id === taskId) {
          return { ...prev, ...updates };
        }
        return prev;
      });
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const handleCreateTaskForDate = (date: Date) => {
    setCalendarTaskDate(date);
    setCalendarTaskDialogOpen(true);
  };



  // Clear selected task if it's been archived or removed from visible columns
  React.useEffect(() => {
    if (selectedTask) {
      const taskExists = state.tasksById[selectedTask.id];
      const isInVisibleColumn = state.columnOrder.some(colId => 
        state.columns[colId]?.includes(selectedTask.id)
      );
      
      // If task doesn't exist or is not in any visible column, clear selection
      if (!taskExists || (!isInVisibleColumn && !state.archived.includes(selectedTask.id))) {
        setSelectedTask(null);
        setTaskDetailOpen(false);
      }
    }
  }, [selectedTask, state.tasksById, state.columns, state.columnOrder, state.archived]);

  // Archive view component
  const ArchiveView = () => (
    <section aria-label="Archived Tasks" className="space-y-6">

      {/* Bulk Actions */}
      {selectedTasks.size > 0 && (
        <div className="flex items-center gap-3 p-3 rounded-lg border bg-muted/50 border-border">
          <span className="text-sm font-medium text-foreground">
            {selectedTasks.size} task{selectedTasks.size !== 1 ? 's' : ''} selected
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleBulkUnarchive} className="border-border text-foreground hover:bg-accent hover:text-accent-foreground">
              <Undo2 className="h-4 w-4 mr-2" />
              Unarchive
            </Button>
            <Button variant="destructive" size="sm" onClick={handleBulkDelete}>
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Permanently
            </Button>
          </div>
        </div>
      )}

      {/* Tasks Count */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {filteredArchivedTasks.length} archived task{filteredArchivedTasks.length !== 1 ? 's' : ''} found
        </p>
      </div>

      {/* Tasks Table */}
      <div className="border rounded-lg border-border bg-card">
        {filteredArchivedTasks.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="text-center text-muted-foreground">
              <p className="text-lg font-medium">No archived tasks found</p>
              <p className="text-sm mt-1">
                {searchTerm || dateFilter !== "1month" 
                  ? "Try adjusting your search or filter criteria"
                  : "Archive some tasks to see them here"
                }
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Table Header */}
            <div className="flex items-center gap-4 p-4 border-b bg-muted/30 border-border">
              <Checkbox
                checked={selectedTasks.size === filteredArchivedTasks.length && filteredArchivedTasks.length > 0}
                onCheckedChange={(checked) => handleSelectAll(!!checked)}
                className="shrink-0"
              />
              <div className="flex-1 grid grid-cols-12 gap-4 items-center">
                <div className="col-span-4 text-sm font-medium text-muted-foreground">Task</div>
                <div className="col-span-2 text-sm font-medium text-muted-foreground">Priority</div>
                <div className="col-span-2 text-sm font-medium text-muted-foreground">Created</div>
                <div className="col-span-2 text-sm font-medium text-muted-foreground">Archived</div>
                <div className="col-span-2 text-sm font-medium text-muted-foreground">Actions</div>
              </div>
            </div>
            
            {/* Table Rows */}
            {filteredArchivedTasks.map((task, index) => (
              <div 
                key={task.id} 
                className={cn(
                  "flex items-center gap-4 p-4 transition-colors hover:bg-muted/50",
                  index !== filteredArchivedTasks.length - 1 && "border-b border-border"
                )}
              >
                <Checkbox
                  checked={selectedTasks.has(task.id)}
                  onCheckedChange={(checked) => handleSelectTask(task.id, !!checked)}
                  className="shrink-0"
                />
                <div className="flex-1 grid grid-cols-12 gap-4 items-center">
                  <div className="col-span-4">
                    <div className="space-y-1">
                      <p className="font-medium text-sm text-foreground">{task.title || 'Untitled Task'}</p>
                      {task.description && (
                        <p className="text-xs line-clamp-2 text-muted-foreground">{task.description}</p>
                      )}
                    </div>
                  </div>
                  <div className="col-span-2">
                    <Badge variant={getPriorityColor(task.priority)} className="text-xs">
                      {getPriorityText(task.priority)}
                    </Badge>
                  </div>
                  <div className="col-span-2">
                    <span className="text-xs text-muted-foreground">
                      {format(new Date(task.createdAt), "MMM dd, yyyy")}
                    </span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-xs text-muted-foreground">
                      {task.archivedAt ? format(new Date(task.archivedAt), "MMM dd, yyyy") : "-"}
                    </span>
                  </div>
                  <div className="col-span-2">
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => unarchiveTask(task.id)}
                        className="h-8 px-2 text-xs text-muted-foreground hover:text-foreground hover:bg-accent"
                      >
                        <Undo2 className="h-3 w-3 mr-1" />
                        Unarchive
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => permanentlyDeleteTask(task.id)}
                        className="h-8 px-2 text-xs text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </section>
  );

  if (loading) {
    return (
      <section aria-label="Kanban Board" className="flex flex-col gap-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <Skeleton className="h-7 w-36" />
          <div className="flex items-center gap-3">
            <Skeleton className="h-9 w-28" />
            <Skeleton className="h-9 w-32" />
            <Skeleton className="h-9 w-36" />
          </div>
        </div>
        <div className="flex gap-4 overflow-x-auto pb-4">
          {[1, 2, 3, 4].map((col) => (
            <div key={col} className="min-w-[320px] w-[350px]">
              <div className="rounded-xl bg-board-column border border-border/40 p-3 flex flex-col gap-3">
                <div className="flex items-center justify-between px-1">
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-4 w-20" />
                    <Skeleton className="h-3 w-4 rounded-full" />
                  </div>
                  <Skeleton className="h-6 w-6 rounded" />
                </div>
                <div className="flex flex-col gap-2">
                  {col === 1 ? (
                    <>
                      <SkeletonTaskCard />
                      <SkeletonTaskCard compact />
                    </>
                  ) : col === 3 ? (
                    <SkeletonTaskCard />
                  ) : null}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section aria-label="Kanban Board" className="space-y-4">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <p className="text-destructive font-medium">Error loading board: {error}</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section aria-label="Kanban Board" className="flex flex-col gap-4">
      {/* Mobile Archive Header */}
      {isMobile && isArchiveView && (
        <div className="flex flex-col gap-3">
          {/* Top row - Back button and filter options */}
          <div className="flex items-center justify-between gap-2">
            {/* Back to Board Button */}
            <Button
              size="sm"
              variant="secondary"
              onClick={toggleArchiveView}
              className="bg-board-column hover:bg-accent border border-border/40 flex-shrink-0 shadow-none"
              aria-label="Back to board"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>

            {/* Filter options */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Search Button */}
              <button
                onClick={() => setArchiveSearchOpen(!archiveSearchOpen)}
                className="flex items-center justify-center w-9 h-9 rounded-lg border bg-board-column border-border/40 text-muted-foreground hover:text-foreground transition-colors shadow-none"
                aria-label="Search archived tasks"
              >
                <Search className="w-4 h-4" />
              </button>

              {/* Date Filter */}
              <Select value={dateFilter} onValueChange={(value) => setDateFilter(value as any)}>
                <SelectTrigger className="h-9 w-[110px] text-sm bg-board-column border-border/40 shadow-none">
                  <SelectValue placeholder="Filter" />
                </SelectTrigger>
                <SelectContent className="bg-popover border-border text-foreground">
                  <SelectItem value="7days">Last 7 days</SelectItem>
                  <SelectItem value="1month">Last month</SelectItem>
                  <SelectItem value="6months">Last 6 months</SelectItem>
                  <SelectItem value="1year">Last year</SelectItem>
                  <SelectItem value="custom">Custom range</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Search input row (expanded) */}
          {archiveSearchOpen && (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search archived tasks..."
                className="w-full h-10 pl-9 pr-8 text-sm rounded-lg border bg-board-column border-border/40 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring shadow-none"
                autoFocus
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground transition-colors"
                >
                  <span className="text-xs leading-none">&times;</span>
                </button>
              )}
            </div>
          )}

          {/* Custom date range picker */}
          {dateFilter === "custom" && (
            <Popover open={showCustomCalendar} onOpenChange={setShowCustomCalendar}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="h-10 w-full justify-start text-left text-sm bg-board-column border-border/40 shadow-none">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {customDateRange?.from ? (
                    customDateRange.to ? (
                      <>{format(customDateRange.from, "MMM d")} – {format(customDateRange.to, "MMM d")}</>
                    ) : format(customDateRange.from, "MMM d, y")
                  ) : <span>Pick a range</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-popover border-border text-foreground" align="center">
                <Calendar
                  initialFocus
                  mode="range"
                  defaultMonth={customDateRange?.from}
                  selected={customDateRange}
                  onSelect={setCustomDateRange}
                  numberOfMonths={1}
                  className={cn("p-3")}
                />
              </PopoverContent>
            </Popover>
          )}
        </div>
      )}

      {/* Mobile Header - Top Row */}
      {isMobile && !isArchiveView && (
        <div className="flex items-center justify-between gap-2 pb-2">
          {/* Left side - Back button */}
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              aria-label="Back to task boards"
              title="Back to task boards"
              className="flex items-center justify-center w-10 h-10 rounded-lg border bg-card border-border text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors flex-shrink-0 shadow-none"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
          )}

          {/* Right side - Action buttons */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {!isArchiveView && (
              <div className="relative flex items-center h-10">
                {boardSearchOpen ? (
                  <div className="relative flex items-center h-10">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
                    <input
                      ref={boardSearchInputRef}
                      type="text"
                      value={boardSearchTerm}
                      onChange={(e) => setBoardSearchTerm(e.target.value)}
                      placeholder="Search..."
                      onBlur={() => { if (!boardSearchTerm) setBoardSearchOpen(false); }}
                      className="w-36 h-full pl-8 pr-7 text-xs rounded-lg border transition-colors bg-card border-border text-foreground placeholder:text-muted-foreground focus:outline-none"
                      autoFocus
                    />
                    <button
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); setBoardSearchTerm(""); setBoardSearchOpen(false); }}
                      className="absolute inset-y-0 right-0 pr-2 flex items-center text-muted-foreground hover:text-foreground"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => { setBoardSearchOpen(true); setTimeout(() => boardSearchInputRef.current?.focus(), 50); }}
                    className="flex items-center justify-center w-10 h-10 rounded-lg border bg-card border-border text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors flex-shrink-0 shadow-none"
                    title="Search tasks"
                    aria-label="Search tasks"
                  >
                    <Search className="w-4 h-4" />
                  </button>
                )}
              </div>
            )}
            {effectiveHeaderConfig && onHeaderUpdate && (
              <HeaderSettingsDialog
                value={effectiveHeaderConfig}
                onSave={onHeaderUpdate}
                currentSlug={currentSlug}
                iconOnly
                columnOrder={state.columnOrder}
                columnMeta={state.columnMeta}
                columnTasks={state.columns}
                onAddColumn={addColumn}
                onDeleteColumn={deleteColumn}
                onReorderColumns={reorderColumnsDB}
                onUpdateColumnTitle={updateColumnTitleDB}
                onUpdateColumnBgColor={updateColumnBgColor}
              />
            )}
            <button
              type="button"
              onClick={toggleArchiveView}
              className="flex items-center justify-center w-10 h-10 rounded-lg border bg-card border-border text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors flex-shrink-0 shadow-none"
              title="Archived"
              aria-label="Archived"
            >
              <Archive className="w-4 h-4" />
            </button>
            <NewTaskDialog
              onAdd={addTask}
              open={newTaskDialogOpen}
              onOpenChange={onNewTaskDialogOpenChange}
              hideTrigger={newTaskDialogOpen !== undefined}
            />
          </div>
        </div>
      )}

      {/* Mobile View Mode Tabs - Bottom Row */}
      {isMobile && !isArchiveView && (
        <div className="flex items-center gap-1 bg-board-column border border-border/40 rounded-lg p-1 w-full">
          <button
            onClick={() => setViewMode("kanban")}
            aria-label="Kanban view"
            className={`flex-1 flex items-center justify-center px-3 py-2 rounded-md text-sm font-medium transition-all ${
              viewMode === "kanban"
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <LayoutDashboard className="h-4 w-4" />
          </button>
          <button
            onClick={() => setViewMode("calendar")}
            aria-label="Calendar view"
            className={`flex-1 flex items-center justify-center px-3 py-2 rounded-md text-sm font-medium transition-all ${
              viewMode === "calendar"
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <CalendarIcon className="h-4 w-4" />
          </button>
          <button
            onClick={() => setViewMode("list")}
            aria-label="Table view"
            className={`flex-1 flex items-center justify-center px-3 py-2 rounded-md text-sm font-medium transition-all ${
              viewMode === "list"
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <Table2 className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Desktop Header */}
      <div className={`flex-col md:flex-row md:items-center md:justify-between gap-3 ${isMobile && !isArchiveView ? 'hidden' : 'flex'}`}>
        {isArchiveView ? (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={toggleArchiveView}
              aria-label="Back to board"
              title="Back to board"
              className="flex items-center justify-center w-10 h-10 rounded-lg border bg-card border-border text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors shadow-none"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <h2 className="text-xl font-semibold text-foreground">Archived Tasks</h2>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            {onBack && (
              <button
                type="button"
                onClick={onBack}
                aria-label="Back to task boards"
                title="Back to task boards"
                className="flex items-center justify-center w-10 h-10 rounded-lg border bg-card border-border text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors shadow-none"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
            )}
            <div className="flex items-center gap-1 bg-board-column border border-border/40 rounded-lg p-1">
              <button
                onClick={() => setViewMode("kanban")}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  viewMode === "kanban"
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <LayoutDashboard className="h-4 w-4" />
                Kanban
              </button>
              <button
                onClick={() => setViewMode("calendar")}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  viewMode === "calendar"
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <CalendarIcon className="h-4 w-4" />
                Calendar
              </button>
              <button
                onClick={() => setViewMode("list")}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  viewMode === "list"
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Table2 className="h-4 w-4" />
                Table
              </button>
            </div>
          </div>
        )}
        <div className="flex items-center gap-2">
          {isArchiveView ? (
            <>
              {/* Expandable Search */}
              <div className="relative flex items-center h-10">
                {archiveSearchOpen ? (
                  <div className="relative flex items-center h-10">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                    <input
                      ref={archiveSearchInputRef}
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search archived tasks..."
                      onBlur={() => { if (!searchTerm) setArchiveSearchOpen(false); }}
                      className="w-56 h-full pl-9 pr-8 text-sm rounded-lg border transition-colors bg-card border-border text-foreground placeholder:text-muted-foreground focus:outline-none"
                      autoFocus
                    />
                    <button
                      onMouseDown={(e) => { e.preventDefault(); setSearchTerm(""); setArchiveSearchOpen(false); }}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <span className="text-xs leading-none">&times;</span>
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => { setArchiveSearchOpen(true); setTimeout(() => archiveSearchInputRef.current?.focus(), 50); }}
                    className="flex items-center justify-center w-10 h-10 rounded-lg border bg-card border-border text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors shadow-none"
                    title="Search archived tasks"
                    aria-label="Search archived tasks"
                  >
                    <Search className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Date Filter */}
              <Select value={dateFilter} onValueChange={(value) => setDateFilter(value as any)}>
                <SelectTrigger className={`h-10 min-w-[130px] text-sm transition-colors focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 outline-none shadow-none ${dateFilter !== '1month' ? 'bg-background shadow-sm text-foreground border-transparent' : 'bg-board-column border-border/40 text-foreground'}`}>
                  <SelectValue placeholder="Filter by date" />
                </SelectTrigger>
                <SelectContent className="bg-popover border-border text-foreground">
                  <SelectItem value="7days">Last 7 days</SelectItem>
                  <SelectItem value="1month">Last month</SelectItem>
                  <SelectItem value="6months">Last 6 months</SelectItem>
                  <SelectItem value="1year">Last year</SelectItem>
                  <SelectItem value="custom">Custom range</SelectItem>
                </SelectContent>
              </Select>

              {/* Custom date range picker */}
              {dateFilter === "custom" && (
                <Popover open={showCustomCalendar} onOpenChange={setShowCustomCalendar}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className={`h-10 justify-start text-left text-sm transition-colors focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 outline-none shadow-none ${customDateRange?.from ? 'bg-background shadow-sm text-foreground border-transparent' : 'bg-board-column border-border/40 text-foreground'}`}>
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {customDateRange?.from ? (
                        customDateRange.to ? (
                          <>{format(customDateRange.from, "MMM d")} – {format(customDateRange.to, "MMM d")}</>
                        ) : format(customDateRange.from, "MMM d, y")
                      ) : <span>Pick a range</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-popover border-border text-foreground" align="end">
                    <Calendar
                      initialFocus
                      mode="range"
                      defaultMonth={customDateRange?.from}
                      selected={customDateRange}
                      onSelect={setCustomDateRange}
                      numberOfMonths={2}
                      className={cn("p-3 pointer-events-auto")}
                    />
                  </PopoverContent>
                </Popover>
              )}
            </>
          ) : (
            <>
              {/* Expandable Search on Board */}
              <div className="relative flex items-center h-10">
                {boardSearchOpen ? (
                  <div className="relative flex items-center h-10">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                    <input
                      ref={boardSearchInputRef}
                      type="text"
                      value={boardSearchTerm}
                      onChange={(e) => setBoardSearchTerm(e.target.value)}
                      placeholder="Search board tasks..."
                      onBlur={() => { if (!boardSearchTerm) setBoardSearchOpen(false); }}
                      className="w-56 h-full pl-9 pr-8 text-sm rounded-lg border transition-colors bg-card border-border text-foreground placeholder:text-muted-foreground focus:outline-none"
                      autoFocus
                    />
                    <button
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); setBoardSearchTerm(""); setBoardSearchOpen(false); }}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground transition-colors"
                      title="Clear search"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => { setBoardSearchOpen(true); setTimeout(() => boardSearchInputRef.current?.focus(), 50); }}
                    className="flex items-center justify-center w-10 h-10 rounded-lg border bg-card border-border text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors shadow-none"
                    title="Search tasks"
                    aria-label="Search tasks"
                  >
                    <Search className="w-4 h-4" />
                  </button>
                )}
              </div>

              {effectiveHeaderConfig && onHeaderUpdate && (
                <HeaderSettingsDialog
                  value={effectiveHeaderConfig}
                  onSave={onHeaderUpdate}
                  currentSlug={currentSlug}
                  iconOnly
                  columnOrder={state.columnOrder}
                  columnMeta={state.columnMeta}
                  columnTasks={state.columns}
                  onAddColumn={addColumn}
                  onDeleteColumn={deleteColumn}
                  onReorderColumns={reorderColumnsDB}
                  onUpdateColumnTitle={updateColumnTitleDB}
                  onUpdateColumnBgColor={updateColumnBgColor}
                />
              )}
              <button
                type="button"
                onClick={toggleArchiveView}
                className="flex items-center justify-center w-10 h-10 rounded-lg border bg-card border-border text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors shadow-none"
                title="Archived"
                aria-label="Archived"
              >
                <Archive className="w-4 h-4" />
              </button>
              <NewTaskDialog
                onAdd={addTask}
                open={newTaskDialogOpen}
                onOpenChange={onNewTaskDialogOpenChange}
                hideTrigger={newTaskDialogOpen !== undefined}
              />
            </>
          )}
        </div>
      </div>
      
      {isArchiveView ? (
        <div className="flex-1 overflow-y-auto">
          <ArchiveView />
        </div>
      ) : viewMode === "calendar" ? (
        <>
          <BoardCalendarView
            state={state}
            onTaskClick={handleTaskClick}
            onTaskUpdate={updateTask}
            onCreateTask={handleCreateTaskForDate}
            searchTerm={boardSearchTerm}
          />
          <NewTaskDialog
            onAdd={(data) => {
              const dueDate = calendarTaskDate ? format(calendarTaskDate, "yyyy-MM-dd") : undefined;
              addTask({ ...data, dueDate });
              setCalendarTaskDialogOpen(false);
              setCalendarTaskDate(null);
            }}
            open={calendarTaskDialogOpen}
            onOpenChange={(open) => {
              setCalendarTaskDialogOpen(open);
              if (!open) setCalendarTaskDate(null);
            }}
            hideTrigger
          />
        </>
      ) : viewMode === "list" ? (
        <BoardListView
          state={state}
          onTaskClick={handleTaskClick}
          onTaskUpdate={updateTask}
          onArchive={archiveTask}
          onDelete={deleteTask}
          searchTerm={boardSearchTerm}
          enableUpvotes={enableUpvotes}
          onToggleUpvote={toggleUpvote}
          currentUserId={user?.id}
        />
      ) : (
        <DragDropContext
          key={`board-${board.id}-tasks-${Object.keys(state.tasksById).length}`}
          onDragEnd={onDragEnd}
        >
          <Droppable droppableId="columns" direction="horizontal" type="COLUMN">
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className="flex overflow-x-auto w-full pb-2 gap-2"
              >
                {state.columnOrder.map((id, index) => {
                  const isCollapsed = collapsedColumns.has(id);

                  return (
                    <Draggable key={id} draggableId={id} index={index}>
                      {(dragProvided) => (
                        <div
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          {...dragProvided.dragHandleProps}
                          className={`${isCollapsed ? "w-14 flex-shrink-0" : "w-[350px] flex-shrink-0"}`}
                        >
                          <Column colId={id} />
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {showAddColumn && (
                  <div className="w-[350px] flex-shrink-0">
                    <AddColumnInline onCreate={(title) => { addColumn(title); setShowAddColumn(false); }} onCancel={() => setShowAddColumn(false)} />
                  </div>
                )}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}
      <TaskDetailDialog
        key={dialogKey}
        task={selectedTask}
        open={taskDetailOpen}
        onOpenChange={(open) => {
          setTaskDetailOpen(open);
          if (!open) {
            // Ensure selected task is cleared when dialog closes
            setSelectedTask(null);
            // Force remount dialog on next open to ensure clean state
            setTimeout(() => setDialogKey(prev => prev + 1), 100);
          }
        }}
        onArchive={archiveTask}
        onDelete={deleteTask}
        onUpdateTask={updateTask}
        columnMeta={state.columnMeta}
        boardName={board.title}
        enableUpvotes={enableUpvotes}
        onToggleUpvote={toggleUpvote}
        currentUserId={user?.id}
      />
    </section>
  );
};
